<template>
  <v-app>
    <AppLoader v-if="state === 'loading'" />
    <SystemError v-else-if="state === 'failed'" :error-code="errorCode" @retry="retry" />
    <slot v-else />
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { AppLoader, SystemError } from '@/apps/core'
import { ApiError } from '@/api/client'
import { useSessionStore } from '../stores/session'

/**
 * Nothing renders until the bootstrap has answered: which platform this is, which sign-in doors
 * it opens, and whether a session exists. The router waits on the same request, so no screen
 * is ever drawn on an unknown session.
 */
const session = useSessionStore()
const router = useRouter()

const state = ref<'loading' | 'ready' | 'failed'>('loading')
const errorCode = ref('')

async function start(): Promise<void> {
  state.value = 'loading'
  try {
    await session.boot()
    state.value = 'ready'
  } catch (error) {
    errorCode.value =
      error instanceof ApiError ? (error.code ?? String(error.status)) : 'NETWORK_ERROR'
    state.value = 'failed'
  }
}

/** Ask again, then navigate again: the guards let the first navigation through unanswered. */
async function retry(): Promise<void> {
  await start()
  if (state.value === 'ready') {
    await router.replace({ path: router.currentRoute.value.fullPath, force: true })
  }
}

onMounted(start)
</script>
